<main class="novice-page">
  <header class="hero">
    <span class="product-label"><mat-icon>account_tree</mat-icon> Shine Connect</span>
    <h1>What should happen automatically?</h1>
    <p>Create a complete workflow with a start and the steps you need. Add a decision only when the work can follow different paths.</p>
  </header>

  <nav class="steps" aria-label="Setup progress">
    <span [class.active]="step >= 1"><b>1</b> Choose</span>
    <span [class.active]="step >= 2"><b>2</b> Add information</span>
    <span [class.active]="step >= 3"><b>3</b> Test safely</span>
  </nav>

  @if (creating) {
    <section class="content narrow" aria-labelledby="create-title">
      <button mat-button type="button" (click)="cancelCreating()"><mat-icon>arrow_back</mat-icon> Back</button>
      <span class="eyebrow">New function</span>
      <h2 id="create-title">What should this function collect?</h2>
      <p class="lead">Give it a familiar name, then choose the information people should provide. Connect builds the safe setup behind the scenes.</p>
      <form [formGroup]="createForm" (ngSubmit)="saveFunction()">
        <mat-form-field appearance="outline">
          <mat-label>Function name</mat-label>
          <input matInput formControlName="name" placeholder="For example: Prepare a partnership request" />
          @if (createForm.controls.name.touched && createForm.controls.name.invalid) {
            <mat-error>Add a short, familiar name.</mat-error>
          }
        </mat-form-field>
        <fieldset class="field-picker">
          <legend>What information is needed?</legend>
          <p>Choose one or more. You can test the function with example information before using it.</p>
          <div class="field-options">
            @for (field of availableFields; track field.key) {
              <mat-checkbox
                [checked]="selectedCreateFields.has(field.key)"
                (change)="toggleCreateField(field.key, $event.checked)">
                <strong>{{field.label}}</strong><span>{{field.help}}</span>
              </mat-checkbox>
            }
          </div>
          @if (createFieldError) {
            <span class="form-error" role="alert">Choose at least one kind of information.</span>
          }
        </fieldset>
        @if (saveError) {
          <div class="form-error callout" role="alert">Connect could not save the function. Check the local environment and try again.</div>
        }
        <div class="actions">
          <button mat-flat-button color="primary" type="submit" [disabled]="saving">
            <mat-spinner [class.hidden]="!saving" diameter="20"></mat-spinner>
            <mat-icon [class.hidden]="saving">add_circle</mat-icon>
            {{saving ? 'Creating…' : 'Create and test'}}
          </button>
        </div>
      </form>
    </section>
  }

  @if (step === 1 && !creating) {
    <section class="content" aria-labelledby="choose-title">
      <div class="section-heading">
        <div><span class="eyebrow">Your automations</span><h2 id="choose-title">Workflows</h2></div>
        <div class="catalog-actions">
          <label class="search"><mat-icon>search</mat-icon><input aria-label="Search workflows and quick actions" placeholder="Search workflows and quick actions" [value]="searchQuery" (input)="updateSearch($event)" /></label>
          <button mat-stroked-button type="button" (click)="openAdvancedWorkflows()"><mat-icon>view_list</mat-icon> View all workflows</button>
          <button mat-flat-button color="primary" type="button" (click)="createWorkflow()"><mat-icon>account_tree</mat-icon> Create a workflow</button>
        </div>
      </div>
      @if (visibleGuidedWorkflows.length) {
        <div class="workflow-grid" aria-label="Saved workflows">
          @for (workflow of visibleGuidedWorkflows; track workflow.workflowId) {
            <button class="workflow-card" type="button" (click)="openWorkflow(workflow)">
              <span class="icon-wrap"><mat-icon>account_tree</mat-icon></span>
              <span><small>Workflow</small><strong>{{workflow.title}}</strong><em>Start → ordered steps → finished</em></span>
              <mat-icon class="arrow">arrow_forward</mat-icon>
            </button>
          }
        </div>
      } @else {
        <button class="workflow-empty" type="button" (click)="createWorkflow()">
          <span class="icon-wrap"><mat-icon>add</mat-icon></span>
          <span><strong>{{searchQuery ? 'No matching workflows' : 'Create your first workflow'}}</strong><em>{{searchQuery ? 'Try another word or search the quick actions below.' : 'Connect will guide you through the start and each step. Add paths only when you need them.'}}</em></span>
          <mat-icon class="arrow">arrow_forward</mat-icon>
        </button>
      }

      <div class="subsection-heading">
        <div><span class="eyebrow">Reusable building blocks</span><h3>Ready-made quick actions</h3><p>Use these on their own for a quick test, or later as steps inside a workflow.</p></div>
        <button mat-stroked-button type="button" (click)="startCreating()"><mat-icon>add</mat-icon> Create a quick action</button>
      </div>
      @if (loading) {
        <div class="loading-state"><mat-spinner diameter="32"></mat-spinner><span>Loading your quick actions…</span></div>
      } @else if (loadError) {
        <div class="empty-state"><mat-icon>cloud_off</mat-icon><strong>Connect is not available yet</strong><span>Start the local Shine environment, then refresh this page.</span></div>
      } @else if (!functions.length) {
        <div class="empty-state"><mat-icon>inbox</mat-icon><strong>No quick actions are ready</strong><span>Create one here or add a maintained workflow configuration.</span></div>
      } @else {
      @if (!visibleFunctions.length) {
        <div class="empty-state"><mat-icon>search_off</mat-icon><strong>No matching quick actions</strong><span>Try another word.</span></div>
      } @else {
      <div class="function-grid">
        @for (item of visibleFunctions; track item.workflowId) {
          <button class="function-card" type="button" (click)="choose(item)">
            <span class="icon-wrap"><mat-icon>{{item.icon}}</mat-icon></span>
            <span class="card-copy"><strong>{{item.title}}</strong><span>{{item.description}}</span></span>
            <mat-icon class="arrow">arrow_forward</mat-icon>
          </button>
        }
      </div>
      }
      }
      <aside class="plain-note"><mat-icon>auto_awesome</mat-icon><span><strong>Built for humans.</strong> The visual flow and the executable workflow are the same thing. Connect hides accounts, credentials and technical references—not the logic.</span></aside>
    </section>
  }

  @if (step === 2 && selected) {
    <section class="content narrow" aria-labelledby="details-title">
      <button mat-button type="button" (click)="back()"><mat-icon>arrow_back</mat-icon> Back</button>
      <span class="eyebrow">{{selected.title}}</span>
      <h2 id="details-title">Add familiar information</h2>
      <p class="lead">Use the example information below or change it. This safe test calls no external service.</p>
      <form [formGroup]="form" (ngSubmit)="review()">
        @for (field of selected.fields; track field.key) {
          <mat-form-field appearance="outline">
            <mat-label>{{field.label}}</mat-label>
            @if (field.options.length) {
              <mat-select [formControlName]="field.key">
                @for (option of field.options; track option) {
                  <mat-option [value]="option">{{option}}</mat-option>
                }
              </mat-select>
            } @else if (field.multiline) {
              <textarea matInput [formControlName]="field.key" rows="4"></textarea>
            } @else {
              <input matInput [formControlName]="field.key" [type]="field.format === 'email' ? 'email' : 'text'" />
            }
          </mat-form-field>
        }
        <div class="actions"><button mat-flat-button color="primary" type="submit">Continue <mat-icon>arrow_forward</mat-icon></button></div>
      </form>
    </section>
  }

  @if (step === 3 && selected) {
    <section class="content narrow" aria-labelledby="review-title">
      <button mat-button type="button" (click)="back()"><mat-icon>arrow_back</mat-icon> Back</button>
      <span class="eyebrow">Ready to test</span>
      <h2 id="review-title">Here’s what Connect will do</h2>
      <div class="flow-summary">
        <span class="flow-icon"><mat-icon>{{selected.icon}}</mat-icon></span>
        <p>{{selected.description}}</p>
        <span class="safety"><mat-icon>verified_user</mat-icon> Safe test</span>
      </div>
      <div class="check-list">
        <span><mat-icon>check_circle</mat-icon> Uses example information only</span>
        <span><mat-icon>check_circle</mat-icon> Calls no external service</span>
        <span><mat-icon>check_circle</mat-icon> Changes no customer data</span>
      </div>
      @if (!result) {
        <button class="run-button" mat-flat-button color="primary" type="button" [disabled]="running" (click)="runSafely()">
          <mat-icon [class.hidden]="running">play_arrow</mat-icon>
          <mat-spinner [class.hidden]="!running" diameter="20"></mat-spinner>
          <span>{{running ? 'Running safe test…' : 'Try it safely'}}</span>
        </button>
      }
      @if (result) {
        <div class="result" [class.error]="result === 'error'" role="status">
          <mat-icon>{{result === 'success' ? 'check_circle' : 'error'}}</mat-icon>
          <div><strong>{{resultTitle}}</strong><p>{{result === 'success' ? 'The configured function ran through Connect successfully.' : 'Check that Connect is available and try again.'}}</p></div>
        </div>
        <button mat-stroked-button type="button" (click)="startOver()">Try another function</button>
      }
    </section>
  }
</main>
